import React, { useState } from 'react';
import { useSimulation } from './hooks/useSimulation';
import { useUpdateSingleRecordMutation } from '../../services/Listing';
import { fmt, fmtEur, fmtPct } from './utils/fmt';
import { getLandTransferTax } from '../../utils/marketDataCalculations';
import SensitivityHeatmap from './SensitivityHeatmap';

/* ── CI Token Map ────────────────────────────────────────────────────────────
 * primary-500:  #1DB3B3   (Teal Brand)
 * primary-700:  #0E7A6B   (Teal Hover)
 * primary-50:   #E8F7F7   (Teal Light)
 * neutral-50:   #F3F3F5
 * neutral-100:  #E0E0DF
 * neutral-200:  #C8C8D0
 * neutral-300:  #ACACB8
 * neutral-400:  #8C8FA0
 * neutral-500:  #838799
 * neutral-600:  #777888
 * neutral-700:  #5D5D6D
 * success-500:  #1FA534
 * error-500:    #E83838
 * warning-500:  #F0C045
 * black:        #1F1F1F
 * ─────────────────────────────────────────────────────────────────────────── */

/* ── Score color helper ─────────────────────────────────────────────────── */
function getScoreColor(score) {
  if (score >= 70) return '#1FA534'; // success-500
  if (score >= 50) return '#F0C045'; // warning-500
  return '#E83838';                  // error-500
}

/* ── Mini Score Donut (48px) ─────────────────────────────────────────────── */
function ScoreDonut({ score, dimmed = false }) {
  const s = score ?? 0;
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const filled = (s / 100) * circumference;
  const color = getScoreColor(s);

  return (
    <div style={{ position: 'relative', width: 48, height: 48, opacity: dimmed ? 0.55 : 1 }}>
      <svg width="48" height="48" viewBox="0 0 48 48" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="24" cy="24" r={radius} fill="none" stroke="#E0E0DF" strokeWidth="4" />
        <circle
          cx="24" cy="24" r={radius} fill="none"
          stroke={color} strokeWidth="4"
          strokeDasharray={`${filled} ${circumference - filled}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.4s ease' }}
        />
      </svg>
      <span style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, color: '#1F1F1F',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {s}
      </span>
    </div>
  );
}

/* ── Score-Impact Card ──────────────────────────────────────────────────── */
function ScoreImpactCard({ baseline, results, isActive, delta }) {
  const baseScore = baseline?.score ?? 0;
  const simScore = isActive && results ? results.simulatedTotal : null;
  const scoreDelta = isActive && delta ? delta.score : null;

  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E0E0DF',
      borderRadius: 16,
      padding: 16,
    }}>
      {/* Section Label */}
      <div style={{
        fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.6px', color: '#8C8FA0', marginBottom: 12,
      }}>
        Score-Impact
      </div>

      {!isActive ? (
        /* State A: No scenario */
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ScoreDonut score={baseScore} />
          <div>
            <div style={{ fontSize: 14, color: '#777888', marginBottom: 2 }}>
              Aktueller Score: <span style={{ fontWeight: 700, color: '#1F1F1F' }}>{baseScore}</span>
            </div>
            <div style={{ fontSize: 13, color: '#8C8FA0' }}>
              Werte verändern, um Score-Impact zu sehen.
            </div>
          </div>
        </div>
      ) : (
        /* State B: Scenario active */
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr auto',
            gap: 12,
            alignItems: 'center',
            marginBottom: 10,
          }}>
            {/* Basis */}
            <div style={{ textAlign: 'center' }}>
              <ScoreDonut score={baseScore} dimmed />
              <div style={{ fontSize: 12, color: '#838799', marginTop: 4 }}>Basis</div>
            </div>

            {/* Simuliert */}
            <div style={{ textAlign: 'center' }}>
              <ScoreDonut score={simScore} />
              <div style={{ fontSize: 12, color: '#838799', marginTop: 4 }}>Simuliert</div>
            </div>

            {/* Delta */}
            <div style={{ textAlign: 'center', minWidth: 56 }}>
              <div style={{
                fontSize: 22, fontWeight: 700,
                color: scoreDelta > 0 ? '#1FA534' : scoreDelta < 0 ? '#E83838' : '#838799',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {scoreDelta > 0 ? '+' : ''}{scoreDelta ?? 0}
              </div>
              <div style={{ fontSize: 12, color: scoreDelta > 0 ? '#1FA534' : scoreDelta < 0 ? '#E83838' : '#838799' }}>
                {scoreDelta > 0 ? '▲ besser' : scoreDelta < 0 ? '▼ schlechter' : '— unverändert'}
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div style={{ borderTop: '1px solid #E0E0DF', paddingTop: 8, marginTop: 2 }}>
            <p style={{ fontSize: 12, color: '#8C8FA0', fontStyle: 'italic', margin: 0 }}>
              * Cashflow + Deal-Faktoren. Lage + Risiko werden nicht neu berechnet.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Kostenstruktur-Balken Card ─────────────────────────────────────────── */
function KostenstrukturCard({ results }) {
  if (!results?.cf) return null;
  const { cf } = results;

  const bewirtschaftung = cf.bewirtschaftung ?? 0;
  const referenz = cf.einnahmen; // 100% reference = Kaltmiete

  const rows = [
    {
      group: 'EINNAHMEN',
      items: [
        { label: 'Kaltmiete', val: cf.einnahmen, barColor: '#1DB3B3', valColor: '#1FA534' },
      ],
    },
    {
      group: 'KOSTEN',
      items: [
        { label: 'Zinsen', val: cf.davonZins, barColor: '#8C8FA0', valColor: '#5D5D6D' },
        { label: 'Tilgung', val: cf.davonTilgung, barColor: '#ACACB8', valColor: '#777888' },
        { label: 'Bewirt.', val: bewirtschaftung, barColor: '#ACACB8', valColor: '#777888' },
      ],
    },
  ];

  const cashflowPositive = cf.cashflow >= 0;

  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E0E0DF',
      borderRadius: 16,
      padding: 16,
    }}>
      {/* Section Label */}
      <div style={{
        fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.6px', color: '#8C8FA0', marginBottom: 12,
      }}>
        Kostenstruktur / Monat
      </div>

      {rows.map((group) => (
        <div key={group.group}>
          {/* Sub-group label */}
          <div style={{
            fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            color: '#8C8FA0', marginBottom: 6, marginTop: group.group === 'KOSTEN' ? 12 : 0,
          }}>
            {group.group}
          </div>
          {group.items.map((item) => (
            <BarRow
              key={item.label}
              label={item.label}
              val={item.val}
              referenz={referenz}
              barColor={item.barColor}
              valColor={item.valColor}
              isIncome={group.group === 'EINNAHMEN'}
            />
          ))}
        </div>
      ))}

      {/* Divider before Cashflow */}
      <div style={{ borderTop: '1.5px solid #C8C8D0', margin: '8px 0' }} />

      {/* Cashflow row */}
      <BarRow
        label="Cashflow"
        val={Math.abs(cf.cashflow)}
        referenz={referenz}
        barColor={cashflowPositive ? '#1FA534' : '#E83838'}
        valColor={cashflowPositive ? '#1FA534' : '#E83838'}
        isIncome={cashflowPositive}
        labelBold
        displayVal={(cashflowPositive ? '+' : '-') + fmt(Math.abs(cf.cashflow)) + ' €'}
      />
    </div>
  );
}

function BarRow({ label, val, referenz, barColor, valColor, isIncome, labelBold = false, displayVal }) {
  const pct = referenz > 0 ? Math.min((Math.abs(val) / referenz) * 100, 100) : 0;
  const formatted = displayVal ?? ((isIncome ? '+' : '-') + fmt(Math.abs(val)) + ' €');

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: 28, gap: 8 }}>
      {/* Label */}
      <span style={{
        width: 72, flexShrink: 0,
        fontSize: 14, color: labelBold ? '#1F1F1F' : '#777888',
        fontWeight: labelBold ? 600 : 400,
      }}>
        {label}
      </span>

      {/* Bar container */}
      <div style={{ flex: 1, position: 'relative', height: 10, display: 'flex', alignItems: 'center' }}>
        <div style={{
          height: 10,
          borderRadius: 4,
          background: barColor,
          width: `${pct}%`,
          minWidth: val > 0 ? 2 : 0,
          transition: 'width 0.3s ease',
        }} />
      </div>

      {/* Value */}
      <span style={{
        width: 72, textAlign: 'right', flexShrink: 0,
        fontSize: 14, fontWeight: 600, color: valColor,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {formatted}
      </span>
    </div>
  );
}

/* ── KPI Grid 2x2 ───────────────────────────────────────────────────────── */
function KpiCard({ label, value, deltaValue, deltaFormat, isActive }) {
  const isNegativeValue = typeof value === 'string' && value.startsWith('-');
  const hasDelta = isActive && deltaValue != null;
  const deltaPositive = deltaValue > 0;
  const deltaZero = deltaValue === 0 || Math.abs(deltaValue) < 0.01;

  let deltaDisplay = null;
  if (hasDelta && !deltaZero) {
    if (deltaFormat === 'eur') {
      deltaDisplay = `${deltaPositive ? '▲ +' : '▼ '}${fmt(Math.round(deltaValue))} €`;
    } else if (deltaFormat === 'pct') {
      deltaDisplay = `${deltaPositive ? '▲ +' : '▼ '}${Math.abs(deltaValue).toFixed(1)}%`;
    } else {
      // factor or plain number
      deltaDisplay = `${deltaPositive ? '▲ +' : '▼ '}${Math.abs(deltaValue).toFixed(1)}`;
    }
  }

  return (
    <div style={{
      background: '#F3F3F5',
      border: '1px solid #E0E0DF',
      borderRadius: 12,
      padding: 12,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600,
        color: '#8C8FA0', marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 18, fontWeight: 700,
        color: isNegativeValue ? '#E83838' : '#1F1F1F',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
      {/* Delta row — visibility: hidden preserves layout when no scenario */}
      <div style={{
        marginTop: 4,
        fontSize: 12, fontWeight: 600,
        visibility: hasDelta && !deltaZero ? 'visible' : 'hidden',
        color: deltaPositive ? '#1FA534' : '#E83838',
      }}>
        {deltaDisplay ?? '—'}
        <span style={{ fontWeight: 400, color: '#8C8FA0', marginLeft: 4 }}>vs. Basis</span>
      </div>
    </div>
  );
}

/* ── Slider Component ───────────────────────────────────────────────────── */
function Slider({ label, value, min, max, step, display, onChange }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-1.5">
        <span style={{ fontSize: 13, fontWeight: 500, color: '#5D5D6D' }}>{label}</span>
        <span style={{
          fontSize: 13, fontWeight: 600, color: '#1F1F1F',
          background: '#E0E0DF', padding: '2px 8px',
          borderRadius: 4, fontVariantNumeric: 'tabular-nums',
        }}>
          {display}
        </span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-[6px] rounded-full appearance-none cursor-pointer outline-none"
        style={{
          background: `linear-gradient(to right, #1DB3B3 0%, #1DB3B3 ${pct}%, #E0E0DF ${pct}%, #E0E0DF 100%)`,
        }}
      />
      <div className="flex justify-between mt-1" style={{ fontSize: 12, color: '#8C8FA0' }}>
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

/* ── DSCR Card ──────────────────────────────────────────────────────────── */
function assessDscr(val) {
  if (val == null) return { rating: 'kritisch', label: 'kritisch', color: '#E83838', bg: '#FDECEC' };
  if (val >= 1.25) return { rating: 'gut', label: 'gut', color: '#1FA534', bg: '#E8F7F7' };
  if (val >= 1.0)  return { rating: 'akzeptabel', label: 'akzeptabel', color: '#F0C045', bg: '#FEF8EC' };
  return { rating: 'kritisch', label: 'kritisch', color: '#E83838', bg: '#FDECEC' };
}

function dscrInterpretation(val, rating) {
  if (val == null) return 'Kein Kapitaldienst vorhanden (Vollfinanzierung nicht berechenbar).';
  if (rating === 'gut') return `Mit einem DSCR von ${val.toFixed(2).replace('.', ',')} deckt die Mieteinnahme den Kapitaldienst um ${Math.round((val - 1) * 100)}% — solider Puffer.`;
  if (rating === 'akzeptabel') return `Mit einem DSCR von ${val.toFixed(2).replace('.', ',')} deckt die Mieteinnahme knapp den Kapitaldienst. Kein Spielraum bei Leerstand.`;
  return `DSCR von ${val.toFixed(2).replace('.', ',')} — die Mieteinnahmen reichen nicht aus, um den Kapitaldienst zu decken. Nachschuss erforderlich.`;
}

function DscrCard({ results, delta, isActive }) {
  const dscr = results?.dscr ?? null;
  const { label, color, bg } = assessDscr(dscr);
  const dscrDelta = isActive && delta?.dscr != null ? delta.dscr : null;
  const interpretation = dscrInterpretation(dscr, assessDscr(dscr).rating);

  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E0E0DF',
      borderRadius: 16,
      padding: 16,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.6px', color: '#8C8FA0', marginBottom: 12,
      }}>
        DSCR — Debt Service Coverage Ratio
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10 }}>
        {/* Value */}
        <div style={{ fontSize: 28, fontWeight: 700, color: '#1F1F1F', fontVariantNumeric: 'tabular-nums' }}>
          {dscr != null ? dscr.toFixed(2).replace('.', ',') : '—'}
        </div>

        {/* Rating badge */}
        <span style={{
          fontSize: 12, fontWeight: 600, borderRadius: 4,
          padding: '2px 8px', background: bg, color,
        }}>
          {label}
        </span>

        {/* Delta */}
        {isActive && dscrDelta != null && Math.abs(dscrDelta) >= 0.01 && (
          <span style={{
            fontSize: 13, fontWeight: 600,
            color: dscrDelta > 0 ? '#1FA534' : '#E83838',
          }}>
            {dscrDelta > 0 ? '▲ +' : '▼ '}{Math.abs(dscrDelta).toFixed(2).replace('.', ',')} vs. Basis
          </span>
        )}
      </div>

      {/* Interpretation */}
      <div style={{ background: '#E8F7F7', borderRadius: 8, padding: '8px 12px' }}>
        <p style={{ fontSize: 13, color: '#4A4A52', margin: 0, lineHeight: 1.5 }}>{interpretation}</p>
      </div>

      {/* Formula hint */}
      <div style={{ marginTop: 8, fontSize: 12, color: '#9A9AA0' }}>
        Formel: NOI (Nettomieteinnahmen p.a.) ÷ Kapitaldienst p.a.
      </div>
    </div>
  );
}

/* ── Toggle Component ───────────────────────────────────────────────────── */
function Toggle({ checked, onChange, label }) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}
    >
      <span style={{ fontSize: 13, fontWeight: 500, color: '#5D5D6D' }}>{label}</span>
      <button
        onClick={() => onChange(!checked)}
        style={{
          width: 40, height: 22, borderRadius: 11,
          background: checked ? '#1DB3B3' : '#C8C8D0',
          border: 'none', cursor: 'pointer', position: 'relative',
          transition: 'background 200ms ease', flexShrink: 0,
        }}
        aria-pressed={checked}
        type="button"
      >
        <span style={{
          position: 'absolute', top: 2,
          left: checked ? 20 : 2,
          width: 18, height: 18, borderRadius: '50%',
          background: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
          transition: 'left 200ms ease',
          display: 'block',
        }} />
      </button>
    </div>
  );
}

/* ── BaseRent Input ─────────────────────────────────────────────────────── */
function BaseRentInput({ value, marketRent, onChange }) {
  const [focused, setFocused] = useState(false);
  const displayVal = value != null ? String(value) : '';
  const placeholder = marketRent != null ? `${fmt(Math.round(marketRent))} (Markt)` : 'Marktmiete';

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: '#5D5D6D' }}>Kaltmiete (manuell)</span>
        {value != null && (
          <button
            onClick={() => onChange(null)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 11, color: '#1DB3B3', fontWeight: 500, padding: 0,
            }}
            type="button"
          >
            Zurücksetzen
          </button>
        )}
      </div>
      <div style={{
        display: 'flex', alignItems: 'center',
        border: `1px solid ${focused ? '#1DB3B3' : '#E0E0DF'}`,
        borderRadius: 6, background: '#FFFFFF', overflow: 'hidden',
        transition: 'border-color 150ms ease',
      }}>
        <input
          type="number"
          value={displayVal}
          placeholder={placeholder}
          min={0} step={10}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const v = e.target.value === '' ? null : parseFloat(e.target.value);
            onChange(v != null && !isNaN(v) && v > 0 ? v : null);
          }}
          style={{
            flex: 1, border: 'none', outline: 'none',
            padding: '6px 10px',
            fontSize: 13, fontWeight: 500, color: '#1F1F1F',
            background: 'transparent',
          }}
        />
        <span style={{
          padding: '6px 10px', fontSize: 13, color: '#838799',
          background: '#F3F3F5', borderLeft: '1px solid #E0E0DF',
          flexShrink: 0,
        }}>
          €/Mo.
        </span>
      </div>
      {value != null && (
        <div style={{ fontSize: 11, color: '#8C8FA0', marginTop: 3 }}>
          Manuell — Marktmiete wird ignoriert
        </div>
      )}
    </div>
  );
}

/* ── Kaufnebenkosten Block ──────────────────────────────────────────────── */
function KaufnebenkostenBlock({ params, setParam, kaufpreis, bundesland }) {
  const [expanded, setExpanded] = useState(false);

  const agentCosts = params.agentCosts ?? 3.57;
  const notaryCosts = params.notaryCosts ?? 2.0;
  const grestOverride = params.grunderwerbSteuer; // null = auto

  // GrESt live berechnen für Anzeige (getLandTransferTax aus Modul-Import)
  const autoGrest = getLandTransferTax ? getLandTransferTax(bundesland) * 100 : null;
  const effectiveGrest = grestOverride != null ? grestOverride : (autoGrest ?? 6.5);

  const gesamtPct = agentCosts + notaryCosts + effectiveGrest;
  const gesamtEur = kaufpreis > 0 ? Math.round(kaufpreis * gesamtPct / 100) : null;

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Collapsed Header */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 0, marginBottom: expanded ? 12 : 0,
        }}
        type="button"
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#8C8FA0', textAlign: 'left' }}>
            Kaufnebenkosten
          </div>
          {!expanded && (
            <div style={{ fontSize: 13, fontWeight: 500, color: '#5D5D6D', marginTop: 2, textAlign: 'left' }}>
              Gesamt: {gesamtPct.toFixed(2)}%
              {gesamtEur != null && <span style={{ color: '#838799', fontWeight: 400 }}> → {fmt(gesamtEur)} €</span>}
            </div>
          )}
        </div>
        <span style={{ fontSize: 12, color: '#1DB3B3', fontWeight: 500 }}>
          {expanded ? 'Einklappen ▴' : 'Anpassen ▾'}
        </span>
      </button>

      {expanded && (
        <div style={{ borderTop: '1px solid #E0E0DF', paddingTop: 12 }}>
          {/* Maklerprovision */}
          <NkInputRow
            label="Maklerprovision"
            value={agentCosts}
            onChange={(v) => setParam('agentCosts', v)}
          />
          {/* Notarkosten */}
          <NkInputRow
            label="Notarkosten"
            value={notaryCosts}
            onChange={(v) => setParam('notaryCosts', v)}
          />
          {/* Grunderwerbsteuer */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#5D5D6D' }}>Grunderwerbsteuer</span>
              {grestOverride != null && (
                <button
                  onClick={() => setParam('grunderwerbSteuer', null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#1DB3B3', fontWeight: 500, padding: 0 }}
                  type="button"
                >
                  ↺ Auf Bundesland zurücksetzen
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1 }}>
                <NkInputRow
                  label={null}
                  value={effectiveGrest}
                  onChange={(v) => setParam('grunderwerbSteuer', v)}
                  style={{ marginBottom: 0 }}
                />
              </div>
              {grestOverride == null && autoGrest != null && (
                <span style={{ fontSize: 11, color: '#838799', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {bundesland || 'Bundesland'}: {autoGrest.toFixed(1)}%
                </span>
              )}
              {grestOverride != null && (
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  background: '#E8F7F7', color: '#0E7A6B',
                  borderRadius: 4, padding: '1px 6px',
                  flexShrink: 0,
                }}>
                  manuell
                </span>
              )}
            </div>
          </div>
          {/* Gesamt */}
          <div style={{
            borderTop: '1px solid #E0E0DF', paddingTop: 8, marginTop: 8,
            display: 'flex', justifyContent: 'space-between',
            fontSize: 13, fontWeight: 600, color: '#1F1F1F',
          }}>
            <span>Gesamt</span>
            <span>
              {gesamtPct.toFixed(2)}%
              {gesamtEur != null && <span style={{ color: '#838799', fontWeight: 400 }}> = {fmt(gesamtEur)} €</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function NkInputRow({ label, value, onChange, style: extraStyle = {} }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: label ? 'flex' : 'block', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, ...extraStyle }}>
      {label && <span style={{ fontSize: 13, fontWeight: 500, color: '#5D5D6D', marginRight: 8, flexShrink: 0 }}>{label}</span>}
      <div style={{
        display: 'flex', alignItems: 'center',
        border: `1px solid ${focused ? '#1DB3B3' : '#E0E0DF'}`,
        borderRadius: 6, overflow: 'hidden',
        background: '#FFFFFF',
        transition: 'border-color 150ms ease',
        minWidth: 80,
      }}>
        <input
          type="number"
          value={value != null ? value : ''}
          min={0} max={20} step={0.01}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v) && v >= 0) onChange(Math.round(v * 100) / 100);
          }}
          style={{
            width: 60, border: 'none', outline: 'none',
            padding: '4px 6px', fontSize: 13, fontWeight: 500,
            color: '#1F1F1F', background: 'transparent',
            fontVariantNumeric: 'tabular-nums',
          }}
        />
        <span style={{
          padding: '4px 6px', fontSize: 12, color: '#838799',
          background: '#F3F3F5', borderLeft: '1px solid #E0E0DF',
        }}>
          %
        </span>
      </div>
    </div>
  );
}

/* ── Save Button ────────────────────────────────────────────────────────── */
function SaveButton({ params, userID, listingId, baseline }) {
  const [updateRecord, { isLoading, isSuccess, isError }] = useUpdateSingleRecordMutation();
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!userID || !listingId) return;
    try {
      await updateRecord({
        UserID: userID,
        ListingID: listingId,
        EquityRatio: params.eigenkapital,
        InterestRate: params.zinssatz,
        Tilgung: params.tilgung,
        TilgungActive: params.tilgungActive ?? false,
        AgentCosts: params.agentCosts,
        NotaryCosts: params.notaryCosts,
        GrunderwerbSteuer: params.grunderwerbSteuer ?? null,
        BaseRent: params.baseRent ?? null,
      }).unwrap();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // Fehler wird durch isError kommuniziert
    }
  };

  return (
    <div style={{ borderTop: '1px solid #E0E0DF', paddingTop: 16, marginTop: 4 }}>
      <button
        onClick={handleSave}
        disabled={isLoading}
        type="button"
        style={{
          width: '100%', padding: '10px 16px',
          background: saved ? '#1FA534' : '#1DB3B3',
          border: 'none', borderRadius: 8, cursor: isLoading ? 'wait' : 'pointer',
          fontSize: 14, fontWeight: 600, color: '#FFFFFF',
          transition: 'background 200ms ease',
          opacity: isLoading ? 0.7 : 1,
        }}
        onMouseEnter={(e) => { if (!isLoading && !saved) e.currentTarget.style.background = '#0E7A6B'; }}
        onMouseLeave={(e) => { if (!saved) e.currentTarget.style.background = '#1DB3B3'; }}
      >
        {isLoading ? 'Wird gespeichert…' : saved ? 'Gespeichert ✓' : 'Einstellungen speichern'}
      </button>
      {isError && (
        <div style={{ fontSize: 12, color: '#E83838', marginTop: 6, textAlign: 'center' }}>
          Speichern fehlgeschlagen. Bitte erneut versuchen.
        </div>
      )}
      <div style={{ fontSize: 11, color: '#9A9AA0', marginTop: 6, textAlign: 'center' }}>
        Gilt dauerhaft für dieses Objekt
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────── */
export default function SimulationView({ baseline, subscores, market, onSwitchToVerstehen, userID, listingId }) {
  const { params, setParam, reset, isActive, results, delta } = useSimulation(baseline, subscores, market);

  if (!params || !baseline) return null;

  const simKaufpreis = Math.round(baseline.kaufpreis * (1 + params.kaufpreisAnpassung / 100));
  // Basis für Mieteanzeige: BaseRent-Override oder Marktmiete
  const baseMiete = params.baseRent != null && params.baseRent > 0
    ? params.baseRent
    : baseline.monatskaltmiete;
  const simMiete = Math.round(baseMiete * (1 + params.mieteAnpassung / 100));

  // Slider badge display: show absolute value only when at baseline, delta + absolute when scenario active
  const kaufpreisDisplay = params.kaufpreisAnpassung === 0
    ? `${fmt(simKaufpreis)} €`
    : `${params.kaufpreisAnpassung > 0 ? '+' : ''}${params.kaufpreisAnpassung}% → ${fmt(simKaufpreis)} €`;

  const mieteDisplay = params.mieteAnpassung === 0
    ? `${fmt(simMiete)} €`
    : `${params.mieteAnpassung > 0 ? '+' : ''}${params.mieteAnpassung}% → ${fmt(simMiete)} €`;

  return (
    <div className="px-8 py-6">

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, lineHeight: '30px', color: '#1F1F1F', marginBottom: 4 }}>
            Simulation
          </h2>
          <p style={{ fontSize: 14, fontWeight: 400, lineHeight: '20px', color: '#838799' }}>
            Verändere Parameter und sieh, wie sich Score und Cashflow verändern.
          </p>
        </div>
        {onSwitchToVerstehen && (
          <button
            onClick={onSwitchToVerstehen}
            style={{
              background: 'transparent', border: 'none',
              color: '#1DB3B3', fontSize: 14, fontWeight: 500,
              cursor: 'pointer', padding: '6px 0', flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#0E7A6B'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#1DB3B3'; }}
          >
            ← Zurück: Analyse
          </button>
        )}
      </div>

      {/* Scenario Banner */}
      {isActive && (
        <div
          className="mb-4 flex items-center justify-between"
          style={{
            background: '#E8F7F7',
            borderLeft: '3px solid #1DB3B3',
            borderRadius: '0 8px 8px 0',
            padding: '10px 16px',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: '#0E7A6B' }}>
            Szenario aktiv — Werte weichen vom Ist-Zustand ab
          </span>
          <button
            onClick={reset}
            style={{
              background: 'transparent', border: 'none',
              fontSize: 14, fontWeight: 500,
              color: '#1DB3B3', cursor: 'pointer',
              padding: '6px 8px', borderRadius: 4,
              transition: 'background 150ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(29,179,179,0.10)';
              e.currentTarget.style.color = '#0E7A6B';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#1DB3B3';
            }}
          >
            Zurücksetzen
          </button>
        </div>
      )}

      {/* Two-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-5">

        {/* Left: Parameter-Panel */}
        <div style={{ background: '#FFFFFF', borderRadius: 16, border: '1px solid #E0E0DF', padding: 20, display: 'flex', flexDirection: 'column' }}>

          {/* ── Deal-Parameter ── */}
          <div style={{
            fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.5px', color: '#8C8FA0', marginBottom: 12,
          }}>
            Deal-Parameter
          </div>

          <Slider
            label="Kaufpreis"
            value={params.kaufpreisAnpassung}
            min={-30} max={20} step={1}
            display={kaufpreisDisplay}
            onChange={(v) => setParam('kaufpreisAnpassung', v)}
          />

          {/* BaseRent-Override */}
          <BaseRentInput
            value={params.baseRent}
            marketRent={baseline.monatskaltmiete}
            onChange={(v) => setParam('baseRent', v)}
          />

          <Slider
            label="Miete anpassen"
            value={params.mieteAnpassung}
            min={-30} max={50} step={1}
            display={mieteDisplay}
            onChange={(v) => setParam('mieteAnpassung', v)}
          />
          <Slider
            label="Leerstand / Jahr"
            value={params.leerstand}
            min={0} max={6} step={1}
            display={params.leerstand === 0 ? '0 Monate' : `${params.leerstand} Monat${params.leerstand > 1 ? 'e' : ''}`}
            onChange={(v) => setParam('leerstand', v)}
          />

          <div style={{ height: 1, background: '#E0E0DF', margin: '16px 0' }} />

          {/* ── Finanzierung ── */}
          <div style={{
            fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.5px', color: '#8C8FA0', marginBottom: 12,
          }}>
            Finanzierung
          </div>

          <Slider
            label="Eigenkapital"
            value={params.eigenkapital}
            min={0} max={40} step={5}
            display={`${params.eigenkapital}%`}
            onChange={(v) => setParam('eigenkapital', v)}
          />
          <Slider
            label="Zinssatz"
            value={params.zinssatz}
            min={2.0} max={6.0} step={0.25}
            display={`${params.zinssatz.toFixed(2)}%`}
            onChange={(v) => setParam('zinssatz', Math.round(v * 100) / 100)}
          />

          {/* Tilgung-Toggle + Slider */}
          <Toggle
            label="Tilgung in Cashflow einbeziehen"
            checked={params.tilgungActive ?? false}
            onChange={(v) => setParam('tilgungActive', v)}
          />
          {params.tilgungActive && (
            <Slider
              label="Tilgungssatz"
              value={params.tilgung}
              min={1.0} max={4.0} step={0.25}
              display={`${params.tilgung.toFixed(2)}%`}
              onChange={(v) => setParam('tilgung', Math.round(v * 100) / 100)}
            />
          )}

          <div style={{ height: 1, background: '#E0E0DF', margin: '16px 0' }} />

          {/* ── Kaufnebenkosten ── */}
          <KaufnebenkostenBlock
            params={params}
            setParam={setParam}
            kaufpreis={simKaufpreis}
            bundesland={baseline.bundesland}
          />

          {/* ── Speichern ── */}
          <SaveButton
            params={params}
            userID={userID}
            listingId={listingId}
            baseline={baseline}
          />
        </div>

        {/* Right: Results Dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* 1. Score-Impact Card */}
          <ScoreImpactCard
            baseline={baseline}
            results={results}
            isActive={isActive}
            delta={delta}
          />

          {/* 2. Kostenstruktur Card */}
          <KostenstrukturCard results={results} />

          {/* 3. KPI Grid 2x2 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <KpiCard
              label="Cashflow / Mo."
              value={results ? fmtEur(results.cf.cashflow) : '—'}
              deltaValue={delta?.cashflow}
              deltaFormat="eur"
              isActive={isActive}
            />
            <KpiCard
              label="EK-Rendite"
              value={params.eigenkapital === 0 ? '∞' : results ? fmtPct(results.ekRendite) : '—'}
              deltaValue={delta?.ekRendite}
              deltaFormat="pct"
              isActive={isActive}
            />
            <KpiCard
              label="Bruttorendite"
              value={results ? fmtPct(results.bruttoRendite) : '—'}
              deltaValue={delta?.bruttoRendite}
              deltaFormat="pct"
              isActive={isActive}
            />
            <KpiCard
              label="Kaufpreisfaktor"
              value={results?.faktor != null ? results.faktor.toFixed(1) : '—'}
              deltaValue={delta?.faktor}
              deltaFormat="plain"
              isActive={isActive}
            />
          </div>

          {/* 4. DSCR Card */}
          <DscrCard results={results} delta={delta} isActive={isActive} />
        </div>
      </div>

      {/* Sensitivity Heatmap — full width */}
      <div className="mt-5">
        <SensitivityHeatmap baseline={baseline} params={params} />
      </div>

      {/* Slider Thumb Styling */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px; height: 20px; border-radius: 50%;
          background: #1DB3B3; cursor: pointer;
          box-shadow: 0 2px 6px rgba(29,179,179,0.35);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px; height: 20px; border-radius: 50%;
          background: #1DB3B3; cursor: pointer; border: none;
          box-shadow: 0 2px 6px rgba(29,179,179,0.35);
        }
      `}</style>
    </div>
  );
}
